import { useState, useEffect, useCallback } from 'react';
import { validateUserForm } from '../validator/userValidator';
import { showToast } from '../utils/toastNotification';

const INITIAL_FORM_STATE = {
    username: '',
    fullName: '',
    email: '',
    phone: '',
    role: '',
    password: '',
    confirmPassword: '',
    active: true
};

export const useUserForm = ({ initialUser = null, isOpen = false }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditMode = !!initialUser;

    useEffect(() => {
        if (isOpen) {
            if (initialUser) {
                setFormData({
                    username: initialUser.username || '',
                    fullName: initialUser.fullName || '',
                    email: initialUser.email || '',
                    phone: initialUser.phone || '',
                    role: initialUser.role || initialUser.vai_tro || '',
                    password: '',
                    confirmPassword: '',
                    active: initialUser.active !== false
                });
            } else {
                setFormData(INITIAL_FORM_STATE);
            }
            setErrors({});
            setIsSubmitting(false);
        }
    }, [isOpen, initialUser]);

    const updateField = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }, [errors]);

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'phone') {
            if (value !== '' && !/^\d*$/.test(value)) return;
            if (value.length > 10) return;
        }
        const fieldValue = type === 'checkbox' ? checked : value;
        updateField(name, fieldValue);
    }, [updateField]);

    const handleBlur = useCallback((e) => {
        const { name, value } = e.target;
        const trimmed = (value || '').trim();
        if (name === 'username') {
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    username: 'Tên đăng nhập là bắt buộc'
                }));
            } else if (trimmed.length < 3) {
                setErrors(prev => ({
                    ...prev,
                    username: 'Tên đăng nhập phải có ít nhất 3 ký tự'
                }));
            } else if (trimmed.length > 50) {
                setErrors(prev => ({
                    ...prev,
                    username: 'Tên đăng nhập không được vượt quá 50 ký tự'
                }));
            } else if (!/^[a-zA-Z0-9_.]+$/.test(trimmed)) {
                setErrors(prev => ({
                    ...prev,
                    username: 'Tên đăng nhập chỉ được chứa chữ cái, số, dấu gạch dưới và dấu chấm'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.username;
                    return newErrors;
                });
            }
        } else if (name === 'fullName') {
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    fullName: 'Họ và tên không được để trống'
                }));
            } else if (trimmed.length < 2) {
                setErrors(prev => ({
                    ...prev,
                    fullName: 'Họ và tên phải có ít nhất 2 ký tự'
                }));
            } else if (trimmed.length > 150) {
                setErrors(prev => ({
                    ...prev,
                    fullName: 'Họ và tên không được vượt quá 150 ký tự'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.fullName;
                    return newErrors;
                });
            }
        } else if (name === 'email') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    email: 'Email là bắt buộc'
                }));
            } else if (!emailRegex.test(trimmed)) {
                setErrors(prev => ({
                    ...prev,
                    email: 'Email không hợp lệ'
                }));
            } else if (trimmed.length > 100) {
                setErrors(prev => ({
                    ...prev,
                    email: 'Email không được vượt quá 100 ký tự'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.email;
                    return newErrors;
                });
            }
        } else if (name === 'phone') {
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    phone: 'Số điện thoại không được để trống'
                }));
            } else if (!/^(03|05|07|08|09)\d{8}$/.test(trimmed)) {
                setErrors(prev => ({
                    ...prev,
                    phone: 'Số điện thoại không hợp lệ (gồm 10 chữ số, bắt đầu bằng 03, 05, 07, 08, 09)'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.phone;
                    return newErrors;
                });
            }
        }
    }, []);

    const validateForm = useCallback(async () => {
        try {
            const result = await validateUserForm(formData, isEditMode, true);
            setErrors(result.errors || {});
            if (!result.isValid) {
                showToast.error('Vui lòng kiểm tra lại các trường bắt buộc!');
            }
            return result.isValid;
        } catch (error) {
            return false;
        }
    }, [formData, isEditMode]);

    const resetForm = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        setErrors({});
        setIsSubmitting(false);
    }, []);

    const prepareSubmitData = useCallback(() => {
        if (isEditMode) {
            return {
                fullName: formData.fullName?.trim() || '',
                phone: formData.phone?.trim() || '',
                role: formData.role,
                username: formData.username?.trim() || '',
                email: formData.email?.trim() || '',
                password: formData.password.trim() || undefined,
            };
        } else {
            return {
                username: formData.username?.trim() || '',
                email: formData.email?.trim() || '',
                fullName: formData.fullName?.trim() || '',
                phone: formData.phone?.trim() || '',
                password: formData.password,
                role: formData.role
            };
        }
    }, [formData, isEditMode]);

    return {
        formData,
        errors,
        isSubmitting,
        isEditMode,

        updateField,
        handleInputChange,
        handleBlur,
        validateForm,
        resetForm,
        prepareSubmitData,
        setIsSubmitting,
        setErrors
    };
};
